import type { UniversalSearchGroup, UniversalSearchItem } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import type mapboxgl from 'mapbox-gl';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { fetchProjects } from '~/api/projectsV3';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { homeMapStore as store } from '~/pages/homeViewV2/store';
import rootStore from '~/store';
import { getAddressDetail, getAddressSuggestions } from './searchUtils';

export function useUniversalSearch() {
  const navigate = useNavigate();
  const { projectDetailsMap, homeMap } = useMap();
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;

  const getItems = async (searchTerm: string): Promise<UniversalSearchGroup[]> => {
    let center: mapboxgl.LngLat | undefined;
    if (homeMap !== undefined) {
      center = homeMap?.getCenter();
    } else if (projectDetailsMap !== undefined) {
      center = projectDetailsMap?.getCenter();
    }

    const organizationId = currentOrganization?.id ?? '';

    const [addressSuggestions, projectsResult] = await Promise.all([
      getAddressSuggestions(searchTerm, center)
        .then((suggestions) => suggestions ?? [])
        .catch(() => []),
      organizationId
        ? fetchProjects(organizationId, { globalFilter: searchTerm, limit: 25, skip: 0 }).catch(() => ({
            projects: [],
            count: 0,
          }))
        : Promise.resolve({ projects: [], count: 0 }),
    ]);

    const addressItems: UniversalSearchItem[] = addressSuggestions.map(({ magicKey, text }) => ({
      label: text,
      value: magicKey,
      onSelect: async () => {
        let addressDetail: Awaited<ReturnType<typeof getAddressDetail>>;
        try {
          addressDetail = await getAddressDetail(text, magicKey);
        } catch (error) {
          console.error('unable to find address', { searchTerm, text, magicKey, error });
          return;
        }
        if (projectDetailsMap) {
          projectDetailsMap.flyTo({
            center: [addressDetail.location.x, addressDetail.location.y],
            zoom: 18,
          });
        } else {
          store.setAddressSearchResult({
            lngLat: [addressDetail.location.x, addressDetail.location.y] as [number, number],
            address: addressDetail.address,
          });
        }
        capturePosthogEvent(POSTHOG_EVENTS.universalSearchResultClicked, {
          type: 'address',
          search_term: searchTerm,
          search_term_length: searchTerm.length,
          magic_key: magicKey,
        });
      },
    }));

    const projectItems: UniversalSearchItem[] = projectsResult.projects.map(({ id, name }) => ({
      label: name,
      value: id,
      onSelect: () => {
        navigate({ to: '/projects/$projectId', params: { projectId: id } });
        capturePosthogEvent(POSTHOG_EVENTS.universalSearchResultClicked, {
          type: 'project',
          search_term: searchTerm,
          search_term_length: searchTerm.length,
          project_id: id,
        });
      },
    }));

    const groups: UniversalSearchGroup[] = [];
    if (projectItems.length > 0) groups.push({ heading: 'Projects', items: projectItems });
    if (addressItems.length > 0) groups.push({ heading: 'Address', items: addressItems });
    return groups;
  };

  const onOpen = () => capturePosthogEvent(POSTHOG_EVENTS.universalSearchOpened, {});

  return { getItems, onOpen };
}
